import { Form, TextField, SubmitFormButton } from '@calendly/form';
import { object, string } from '@calendly/form/validation';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy';
import { AddMiniIcon, CheckCircleIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useEffect, forwardRef } from 'react';
import { useBooleanState } from 'hooks/useBooleanState';
import { useToggleState } from 'hooks/useToggleState';
const flexContainerCss = "f9qvvdd";
const FlexContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexContainerCss, props.className)} />;
});
const formControlCss = "f161n21w";
const FormControl = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formControlCss, props.className)} />;
});
const buttonTextCss = "bj4o2yf";
const ButtonText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(buttonTextCss, props.className)} />;
});
const textFieldsContainerIsPhoneCss = "t1u47unk";
const textFieldsContainerCss = "thb16c8";
const TextFieldsContainer = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <FlexContainer ref={ref} {...props} className={classNames(textFieldsContainerCss, {
    [textFieldsContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledTextFieldIsPhoneCss = "sguzg90";
const styledTextFieldCss = "sqixgji";
const StyledTextField = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <TextField ref={ref} {...props} className={classNames(styledTextFieldCss, {
    [styledTextFieldIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledButtonCss = "s1ypflc5";
const StyledButton = forwardRef((props, ref) => <MinimalButton ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const addInviteeSuccessContainerFadeOutCss = "a118ebos";
const addInviteeSuccessContainerCss = "a10vt7ua";
const AddInviteeSuccessContainer = forwardRef((props, ref) => {
  const {
    fadeOut,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(addInviteeSuccessContainerCss, {
    [addInviteeSuccessContainerFadeOutCss]: fadeOut
  }, props.className)} />;
});
const inviteeAddedTextCss = "i1544ue2";
const InviteeAddedText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(inviteeAddedTextCss, props.className)} />;
});
const errorMessageCss = "e1rpdah4";
const ErrorMessage = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(errorMessageCss, props.className)} />;
});
const AddInviteeForm = ({
  toggleOpen,
  showSuccess,
  allInvitees,
  addInvitee
}) => {
  const inviteeEmails = allInvitees.map(invitee => invitee.email);
  const initialValues = {
    fullName: '',
    email: ''
  };
  const validationSchema = object({
    fullName: string().required(),
    email: string().email().required().test('email', I18n.t('publisher.scheduled_events.pending.booking_modal.duplicate_email_error'), value => !inviteeEmails.includes(value))
  });
  return <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={values => {
    addInvitee(values);
    showSuccess();
    toggleOpen();
  }}>
      <TextFieldsContainer>
        <FullStoryExclude>
          <StyledTextField name="fullName" type="fullName" label={I18n.t('publisher.organization.headers.name')} required />
        </FullStoryExclude>
        <FullStoryExclude>
          <StyledTextField name="email" type="email" label={I18n.t('publisher.labels.email')} required />
        </FullStoryExclude>
      </TextFieldsContainer>
      <FormControl>
        <Button decoration="ghost" size="small" onClick={toggleOpen}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <SubmitFormButton size="small">
          {I18n.t('publisher.scheduled_events.pending.booking_modal.add_invitee')}
        </SubmitFormButton>
      </FormControl>
    </Form>;
};
const MaxInviteesErrorMessage = () => <ErrorMessage>
    {I18n.t('publisher.scheduled_events.pending.booking_modal.max_invitees_error')}
  </ErrorMessage>;
const AddInviteeControl = ({
  handleClick,
  success,
  hideSuccess
}) => <FlexContainer>
    <StyledButton onClick={handleClick}>
      <AddMiniIcon />
      <ButtonText>
        {I18n.t('publisher.scheduled_events.pending.booking_modal.add_invitee')}
      </ButtonText>
    </StyledButton>
    <AddInviteeSuccessContainer fadeOut={success} onAnimationEnd={hideSuccess}>
      <CheckCircleIcon spacingRight={8} />
      <InviteeAddedText>
        {I18n.t('publisher.scheduled_events.pending.booking_modal.invitee_added')}
      </InviteeAddedText>
    </AddInviteeSuccessContainer>
  </FlexContainer>;
export const AddInvitee = ({
  allInvitees,
  addInvitee
}) => {
  const INVITEES_LIMIT = 40;
  const [isOpen, toggleOpen] = useToggleState(true);
  const [success, showSuccess, hideSuccess] = useBooleanState(false);
  const [showMaxInviteesError, setShowMaxInviteesError] = useState(false);
  const maxInviteesReached = allInvitees.length >= INVITEES_LIMIT;
  const handleClick = () => {
    if (maxInviteesReached) {
      setShowMaxInviteesError(true);
    } else {
      hideSuccess();
      toggleOpen();
    }
  };
  useEffect(() => !maxInviteesReached && setShowMaxInviteesError(false), [maxInviteesReached]);
  return <>
      <div>
        {isOpen ? <AddInviteeControl handleClick={handleClick} success={success} hideSuccess={hideSuccess} /> : <AddInviteeForm toggleOpen={toggleOpen} showSuccess={showSuccess} allInvitees={allInvitees} addInvitee={addInvitee} />}
      </div>
      {showMaxInviteesError && <MaxInviteesErrorMessage />}
    </>;
};

require("./add_invitee.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./add_invitee.js");